import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Calendar as CalendarIcon, Filter, RefreshCw, Check, X } from 'lucide-react';
import api from '../utils/api';
import { format } from 'date-fns';

const SchedulePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookingRequests, setBookingRequests] = useState({
    pending: [],
    confirmed: [],
    rejected: []
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBookingRequests();
  }, []);

  const fetchBookingRequests = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch all user assets
      const response = await api.get('/api/user-assets');
      const allAssets = response.data;
      
      // Fetch users and assets for display names
      const [usersResponse, assetsResponse] = await Promise.all([
        api.get('/api/admin/users'),
        api.get('/api/assets')
      ]);
      
      const usersMap = {};
      usersResponse.data.forEach(user => {
        usersMap[user.id] = user;
      });
      
      const assetsMap = {};
      assetsResponse.data.forEach(asset => {
        assetsMap[asset.id] = asset;
      });
      
      // Enrich user assets with user and asset details
      const enrichedAssets = allAssets.map(ua => {
        const user = usersMap[ua.user_id] || {};
        const asset = assetsMap[ua.asset_id] || {};
        return {
          ...ua,
          user_name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User',
          user_email: user.email || 'N/A',
          asset_name: asset.name || 'Unknown Asset',
          asset_identification_number: asset.asset_identification_number || 'N/A'
        };
      });
      
      // Group by booking status
      const grouped = {
        pending: enrichedAssets.filter(a => a.next_booking_status === 'pending'),
        confirmed: enrichedAssets.filter(a => a.next_booking_status === 'confirmed'),
        rejected: enrichedAssets.filter(a => a.next_booking_status === 'rejected')
      };
      
      setBookingRequests(grouped);
    } catch (err) {
      console.error('Error fetching booking requests:', err);
      setError(err.response?.data?.detail || 'Failed to load booking requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userAssetId) => {
    const confirmed = window.confirm('Are you sure you want to approve this booking request?');
    if (!confirmed) return;
    
    try {
      await api.put(`/api/user-assets/${userAssetId}/status`, {
        next_booking_status: 'confirmed'
      });
      
      alert('Booking approved successfully!');
      await fetchBookingRequests();
    } catch (error) {
      console.error('Error approving booking:', error);
      alert(`Failed to approve booking: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleReject = async (userAssetId) => {
    const confirmed = window.confirm('Are you sure you want to reject this booking request?');
    if (!confirmed) return;
    
    try {
      await api.put(`/api/user-assets/${userAssetId}/status`, {
        next_booking_status: 'rejected'
      });
      
      alert('Booking rejected');
      await fetchBookingRequests();
    } catch (error) {
      console.error('Error rejecting booking:', error);
      alert(`Failed to reject booking: ${error.response?.data?.detail || error.message}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch {
      return 'Invalid Date';
    }
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 'N/A';
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      return `${days} day${days !== 1 ? 's' : ''}`;
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2"
                size="sm"
              >
                <ArrowLeft size={16} />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Scheduling</h1>
                <p className="text-sm text-gray-600 mt-1">Manage asset booking requests and availability</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchBookingRequests}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-4 text-gray-600">Loading booking requests...</p>
          </div>
        ) : (
          <>
            {/* Pending Requests Section */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <span className="text-2xl">🟡</span> Pending Requests ({bookingRequests.pending.length})
              </h2>
              
              {bookingRequests.pending.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-gray-500">
                    No pending requests
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {bookingRequests.pending.map((request) => (
                    <Card key={request.id} className="bg-yellow-50 border border-yellow-300">
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-gray-900">
                              {request.asset_name}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Asset ID: {request.asset_identification_number}
                            </p>
                            <p className="text-sm text-gray-600">
                              User: {request.user_name} ({request.user_email})
                            </p>
                          </div>
                        </div>
                        
                        <div className="mb-4 p-3 bg-white rounded-lg border border-yellow-200">
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Requested Dates:
                          </p>
                          <p className="text-base font-semibold text-yellow-800">
                            {formatDate(request.next_booking_start)} - {formatDate(request.next_booking_end)}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            Duration: {calculateDuration(request.next_booking_start, request.next_booking_end)}
                          </p>
                        </div>
                        
                        <div className="flex gap-3">
                          <Button
                            onClick={() => handleApprove(request.id)}
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check size={16} />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleReject(request.id)}
                            variant="outline"
                            className="flex items-center gap-2 border-red-600 text-red-600 hover:bg-red-50"
                          >
                            <X size={16} />
                            Reject
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>

            {/* Confirmed Bookings Section */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <span className="text-2xl">✅</span> Confirmed Bookings ({bookingRequests.confirmed.length})
              </h2>
              
              {bookingRequests.confirmed.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-gray-500">
                    No confirmed bookings
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {bookingRequests.confirmed.map((booking) => (
                    <Card key={booking.id} className="bg-green-50 border border-green-300">
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-gray-900">
                              {booking.asset_name}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Asset ID: {booking.asset_identification_number}
                            </p>
                            <p className="text-sm text-gray-600">
                              User: {booking.user_name}
                            </p>
                            <div className="mt-3 flex items-center gap-2 text-green-700">
                              <CalendarIcon size={16} />
                              <span className="font-medium">
                                {formatDate(booking.next_booking_start)} - {formatDate(booking.next_booking_end)}
                              </span>
                            </div>
                          </div>
                          <span className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-full">
                            <Check size={14} className="mr-1" />
                            Confirmed
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>

            {/* Rejected Requests Section */}
            <section>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <span className="text-2xl">❌</span> Rejected Requests ({bookingRequests.rejected.length})
              </h2>
              
              {bookingRequests.rejected.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-gray-500">
                    No rejected requests
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {bookingRequests.rejected.map((request) => (
                    <Card key={request.id} className="bg-red-50 border border-red-300">
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-gray-900">
                              {request.asset_name}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Asset ID: {request.asset_identification_number}
                            </p>
                            <p className="text-sm text-gray-600">
                              User: {request.user_name}
                            </p>
                            <div className="mt-3 flex items-center gap-2 text-red-700">
                              <CalendarIcon size={16} />
                              <span className="font-medium">
                                {formatDate(request.next_booking_start)} - {formatDate(request.next_booking_end)}
                              </span>
                            </div>
                          </div>
                          <span className="inline-flex items-center px-3 py-1 bg-red-600 text-white text-xs font-medium rounded-full">
                            <X size={14} className="mr-1" />
                            Rejected
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default SchedulePage;
